<template>
  <apple-pay-button
    v-if="showApplePayButton"
    :buttonstyle="color"
    type="buy"
    :locale="lang"
    :disabled="disabled"
    @click="onClick"
    id="apple-pay-button"
  ></apple-pay-button>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { Raven } from 'vue-raven'
import { ToolsClass } from '@/base/class/tools.class'
import { MESSAGES } from '@/base/const/texts.const'

export default {
  name: 'ApplePay',
  props: {
    color: {
      type: String,
      required: false,
      default: 'black',
      validator: function (value) {
        return ['black', 'white', 'automatic'].includes(value)
      }
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    marketing: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    showApplePayButton: false,
    session: null,
    sessionState: null // null | 'started' | 'completed' | 'aborted'
  }),
  computed: {
    ...mapState({
      paymentData: state => state.payment.paymentData,
      lang: state => state.lang.current
    }),
    merchantIdentifier: function () {
      return process.env.VUE_APP_APPLEPAY_MERCHANT_ID
    }
  },
  methods: {
    ...mapActions([
      'getApplePaySession',
      'sendPaymentUpdateTrx',
      'disableSessionTimer',
      'addModalInfo'
    ]),
    async onClick () {
      const form = this.$parent.form
      if (!form || this.disabled) {
        return
      }
      form.setAsSubmitted()
      if (form.valid) {
        await this.startSession()
      }
    },
    async startSession () {
      // Jeśli sesja już jest aktywna, nie twórz nowej
      if (this.session && this.sessionState === 'started') {
        return
      }

      const centValue = ToolsClass.getAmountAsCurrencyInteger(
        this.paymentData.transaction.payAmount
      )
      const stringToHash = centValue + '|' + this.paymentData.transaction.payAmountCurrency
      const request = {
        merchantIdentifier: this.merchantIdentifier,
        countryCode: 'PL',
        currencyCode: this.paymentData.transaction.payAmountCurrency || 'PLN',
        total: {
          label: this.paymentData.transaction.orderId,
          amount: (this.paymentData.transaction.payAmount ?? 0).toFixed(2)
        },
        supportedNetworks: ['visa', 'masterCard'],
        merchantCapabilities: ['supports3DS'],
        applicationData: btoa(stringToHash)
      }

      this.session = new window.ApplePaySession(4, request)
      this.sessionState = 'started'

      this.session.onvalidatemerchant = this.onValidateMerchant
      this.session.onpaymentauthorized = this.onPaymentAuthorized
      this.session.oncancel = this.onCancel

      this.session.begin()
    },
    async onValidateMerchant (event) {
      if (ToolsClass.isDebugEnabled()) {
        console.log('onValidateMerchant', JSON.stringify(event))
      }
      try {
        const response = await this.getApplePaySession(event.validationURL)
        if (ToolsClass.isDebugEnabled()) {
          console.log('response', JSON.stringify(response))
        }
        this.session.completeMerchantValidation(response.body)
      } catch (error) {
        Raven.captureMessage('ApplePay onValidateMerchant error', { extra: error })
        this.abortSession()
      }
    },
    async onPaymentAuthorized (event) {
      if (ToolsClass.isDebugEnabled()) {
        console.log('onPaymentAuthorized', JSON.stringify(event))
      }
      if (!event.payment?.token) {
        if (this.session) {
          this.session.completePayment({
            status: window.ApplePaySession.STATUS_FAILURE,
            errors: [new window.ApplePayError('unknown')]
          })
        }
        this.endSession()
        return
      }

      this.disableSessionTimer()

      const token = btoa(JSON.stringify(event.payment.token.paymentData))
      const data = {
        paymentToken: token,
        marketing: this.marketing
      }
      if (ToolsClass.isDebugEnabled()) {
        console.log('data', JSON.stringify(data))
      }
      try {
        await this.sendPaymentUpdateTrx(data)
        if (this.session) {
          this.session.completePayment({ status: window.ApplePaySession.STATUS_SUCCESS })
        }
        this.endSession()
      } catch (error) {
        if (this.session) {
          this.session.completePayment({
            status: window.ApplePaySession.STATUS_FAILURE,
            errors: [new window.ApplePayError('unknown')]
          })
        }
        this.endSession()
        Raven.captureMessage('ApplePay sendPaymentUpdateTrx error', { extra: error })
      }
    },
    onCancel () {
      if (ToolsClass.isDebugEnabled()) {
        console.log('onCancel')
      }
      this.abortSession()
      this.showCancelModal()
    },
    // zarządzanie sesją
    endSession () {
      this.sessionState = 'completed'
      this.session = null
    },
    abortSession () {
      if (this.session) {
        // Odpiąć handlery zanim abort() wywoła oncancel
        this.session.oncancel = null
        this.session.onvalidatemerchant = null
        this.session.onpaymentauthorized = null
        this.session.abort()
        this.sessionState = 'aborted'
        this.session = null
      }
    },
    showCancelModal () {
      const message = {
        text: MESSAGES().errors.applePayCancel,
        focusReturnElementId: 'apple-pay-button',
        options: {
          title: MESSAGES().errors.applePayCancelTitle,
          size: 'medium'
        }
      }
      this.addModalInfo(message)
    }
  },
  mounted () {
    if (!window.ApplePaySession) return

    if (typeof window.ApplePaySession.applePayCapabilities === 'function') {
      window.ApplePaySession
        .applePayCapabilities(this.merchantIdentifier)
        .then(({ paymentCredentialStatus }) => {
          this.showApplePayButton = paymentCredentialStatus !== 'applePayUnsupported'
        })
        .catch(() => {
          this.showApplePayButton = false
        })
    } else {
      // fallback dla starszych Safari
      this.showApplePayButton = window.ApplePaySession.canMakePayments()
    }
  },
  beforeDestroy () {
    this.abortSession()
  }
}
</script>

<style scoped>
apple-pay-button {
  --apple-pay-button-width: 240px;
  --apple-pay-button-height: 40px;
  --apple-pay-button-border-radius: 3px;
  --apple-pay-button-padding: 0px 0px;
  --apple-pay-button-box-sizing: border-box;
}
</style>
